import { useEffect, useState } from "react";
import type { FeatureCollection, GeoJsonProperties, Geometry } from "geojson";
import { Layer, Source, useMap } from "react-map-gl/mapbox";

import { GRID_TRIAL_LABEL_PIXEL_OFFSET } from "context/PlotMap/gridUtils";

import {
  COLORED_PLOT_FILL_OPACITY,
  DEFAULT_PLOT_FILL_OPACITY,
  dimmedOpacity,
  plotFillColor,
} from "./utils/display";
import { LABEL_PLAQUE_IMAGE_IDS, labelPlaqueLayout } from "./utils/labelPlaque";
import {
  BOUNDARY_FIRST_POINT_LAYER_ID,
  BOUNDARY_LABEL_LAYER_ID,
  BOUNDARY_LINE_LAYER_ID,
  BOUNDARY_VERTEX_LAYER_ID,
  GRID_BBOX_LAYER_ID,
  GRID_CELL_LAYER_ID,
  GRID_HANDLE_CORNER_LAYER_ID,
  GRID_LABEL_LAYER_ID,
  GRID_LINE_LAYER_ID,
  GRID_ROW_LABEL_LAYER_ID,
  GRID_ROW_LINE_LAYER_ID,
  GRID_TRIAL_LABEL_LAYER_ID,
  GRID_VERTEX_LAYER_ID,
  GROWER_BOUNDARY_DRAFT_LABEL_LAYER_ID,
  GROWER_BOUNDARY_DRAFT_LINE_LAYER_ID,
  ROW_BOUNDARY_LABEL_LAYER_ID,
  ROW_BOUNDARY_LINE_LAYER_ID,
  ROW_FILL_LAYER_ID,
  SAVED_PLOTS_FILL_LAYER_ID,
  SAVED_PLOTS_LABEL_LAYER_ID,
  SAVED_PLOTS_LINE_LAYER_ID,
  SAVED_PLOTS_TRIAL_LABEL_LAYER_ID,
  SITE_BOUNDARY_DRAFT_LABEL_LAYER_ID,
  SITE_BOUNDARY_DRAFT_LINE_LAYER_ID,
  SITE_BOUNDARY_FIRST_POINT_LAYER_ID,
  SITE_BOUNDARY_LABEL_LAYER_ID,
  SITE_BOUNDARY_LINE_LAYER_ID,
  SITE_BOUNDARY_VERTEX_LAYER_ID,
} from "./utils/layerPrefs";
import {
  BOUNDARY_COLOR,
  EMPTY_FEATURE_COLLECTION,
  LABEL_FILL_COLOR,
  PLOT_LABEL_MIN_ZOOM,
  ROW_BOUNDARY_COLOR,
  ROW_LABEL_MIN_ZOOM,
  SITE_BOUNDARY_COLOR,
} from "./utils/mapConstants";

type MapFeatureCollection = FeatureCollection<Geometry, GeoJsonProperties>;
type MapVisibility = "none" | "visible";

interface PlotMapLayersProps {
  bboxGeoJson: MapFeatureCollection | null;
  boundaryVisibility: MapVisibility;
  gridGeoJson: MapFeatureCollection;
  growerBoundariesGeoJson: MapFeatureCollection;
  growerDraftGeoJson: MapFeatureCollection;
  growerDraftLabelVisibility: MapVisibility;
  growerDraftVisibility: MapVisibility;
  growerLabelVisibility: MapVisibility;
  isPlotGridMode: boolean;
  plotGridVisibility: MapVisibility;
  plotLabelVisibility: MapVisibility;
  rowBoundariesGeoJson: MapFeatureCollection;
  rowBoundaryVisibility: MapVisibility;
  rowFillGeoJson: MapFeatureCollection;
  rowLabelVisibility: MapVisibility;
  savedPlotsGeoJson: MapFeatureCollection;
  showRowVertices: boolean;
  siteBoundariesGeoJson: MapFeatureCollection;
  siteBoundaryVisibility: MapVisibility;
  siteDraftGeoJson: MapFeatureCollection;
  siteDraftLabelVisibility: MapVisibility;
  siteDraftVisibility: MapVisibility;
  siteLabelVisibility: MapVisibility;
}

const BOUNDARY_SOURCE_ID = "plot-boundary";
const GROWER_BOUNDARY_DRAFT_SOURCE_ID = "plot-grower-boundary-draft";
const SITE_BOUNDARY_SOURCE_ID = "plot-site-boundary";
const SITE_BOUNDARY_DRAFT_SOURCE_ID = "plot-site-boundary-draft";
const GRID_SOURCE_ID = "plot-grid";
const GRID_BBOX_SOURCE_ID = "plot-grid-bbox";
const ROW_BOUNDARY_SOURCE_ID = "plot-row-boundary";
const ROW_FILL_SOURCE_ID = "plot-row-fill";
const SAVED_PLOTS_SOURCE_ID = "plot-saved-plots";

const SOURCE_IDS = [
  BOUNDARY_SOURCE_ID,
  GROWER_BOUNDARY_DRAFT_SOURCE_ID,
  SITE_BOUNDARY_SOURCE_ID,
  SITE_BOUNDARY_DRAFT_SOURCE_ID,
  ROW_BOUNDARY_SOURCE_ID,
  ROW_FILL_SOURCE_ID,
  SAVED_PLOTS_SOURCE_ID,
  GRID_SOURCE_ID,
  GRID_BBOX_SOURCE_ID,
];

/**
 * Re-render once the style can accept sources. Geometry layers are added
 * top to bottom. Label layers are inserted above that geometry, keeping
 * their own order: plot, row, trial, site, then grower.
 */
const useRerenderUntilMapSourcesExist = (): void => {
  const { default: mapRef } = useMap();
  const [, setStyleRevision] = useState(0);

  useEffect(() => {
    const map = mapRef?.getMap();
    if (map == null) {
      return;
    }

    const syncSources = () => {
      const missingSource = SOURCE_IDS.some(
        (sourceId) => map.getSource(sourceId) == null,
      );
      if (missingSource) {
        setStyleRevision((revision) => revision + 1);
      }
    };

    map.on("styledata", syncSources);
    syncSources();
    return () => {
      map.off("styledata", syncSources);
    };
  }, [mapRef]);
};

const PlotMapLayers: React.FC<PlotMapLayersProps> = ({
  bboxGeoJson,
  boundaryVisibility,
  gridGeoJson,
  growerBoundariesGeoJson,
  growerDraftGeoJson,
  growerDraftLabelVisibility,
  growerDraftVisibility,
  growerLabelVisibility,
  isPlotGridMode,
  plotGridVisibility,
  plotLabelVisibility,
  rowBoundariesGeoJson,
  rowBoundaryVisibility,
  rowFillGeoJson,
  rowLabelVisibility,
  savedPlotsGeoJson,
  showRowVertices,
  siteBoundariesGeoJson,
  siteBoundaryVisibility,
  siteDraftGeoJson,
  siteDraftLabelVisibility,
  siteDraftVisibility,
  siteLabelVisibility,
}) => {
  useRerenderUntilMapSourcesExist();
  const gridVisibility = isPlotGridMode ? plotGridVisibility : "none";
  const gridRowVisibility = isPlotGridMode ? rowBoundaryVisibility : "none";
  const gridRowLabelVisibility = isPlotGridMode ? rowLabelVisibility : "none";
  const gridLabelVisibility = isPlotGridMode ? plotLabelVisibility : "none";

  return (
    <>
      <Source
        data={growerBoundariesGeoJson}
        id={BOUNDARY_SOURCE_ID}
        type="geojson"
      />
      <Source
        data={growerDraftGeoJson}
        id={GROWER_BOUNDARY_DRAFT_SOURCE_ID}
        type="geojson"
      />
      <Source
        data={siteBoundariesGeoJson}
        id={SITE_BOUNDARY_SOURCE_ID}
        type="geojson"
      />
      <Source
        data={siteDraftGeoJson}
        id={SITE_BOUNDARY_DRAFT_SOURCE_ID}
        type="geojson"
      />
      <Source
        data={rowBoundariesGeoJson}
        id={ROW_BOUNDARY_SOURCE_ID}
        type="geojson"
      />
      <Source data={rowFillGeoJson} id={ROW_FILL_SOURCE_ID} type="geojson" />
      <Source
        data={savedPlotsGeoJson}
        id={SAVED_PLOTS_SOURCE_ID}
        type="geojson"
      />
      <Source data={gridGeoJson} id={GRID_SOURCE_ID} type="geojson" />
      <Source
        data={bboxGeoJson ?? EMPTY_FEATURE_COLLECTION}
        id={GRID_BBOX_SOURCE_ID}
        type="geojson"
      />
      <Layer
        id={GRID_BBOX_LAYER_ID}
        layout={{
          visibility: bboxGeoJson != null ? plotGridVisibility : "none",
        }}
        paint={{
          "line-color": "#ffffff",
          "line-dasharray": [2, 1],
          "line-width": 2,
        }}
        source={GRID_BBOX_SOURCE_ID}
        type="line"
      />
      <Layer
        beforeId={GRID_BBOX_LAYER_ID}
        filter={
          showRowVertices
            ? ["==", ["get", "kind"], "grid-vertex"]
            : [
                "all",
                ["==", ["get", "kind"], "grid-vertex"],
                ["!=", ["get", "role"], "row"],
              ]
        }
        id={GRID_VERTEX_LAYER_ID}
        layout={{ visibility: gridVisibility }}
        paint={{
          "circle-color": [
            "case",
            ["==", ["get", "selected"], true],
            "#ffffff",
            ["==", ["get", "role"], "row"],
            ROW_BOUNDARY_COLOR,
            "#00e5ff",
          ],
          "circle-radius": [
            "case",
            [
              "all",
              ["==", ["get", "selected"], true],
              ["==", ["get", "role"], "row"],
            ],
            3.5,
            ["==", ["get", "selected"], true],
            7,
            ["==", ["get", "role"], "row"],
            2.5,
            5,
          ],
          "circle-stroke-color": [
            "case",
            [
              "all",
              ["==", ["get", "selected"], true],
              ["==", ["get", "role"], "row"],
            ],
            ROW_BOUNDARY_COLOR,
            ["==", ["get", "selected"], true],
            "#00e5ff",
            "#000000",
          ],
          "circle-stroke-width": [
            "case",
            ["==", ["get", "selected"], true],
            2,
            1.5,
          ],
        }}
        source={GRID_SOURCE_ID}
        type="circle"
      />
      <Layer
        beforeId={GRID_VERTEX_LAYER_ID}
        filter={[
          "all",
          ["==", ["get", "kind"], "grid-handle"],
          ["==", ["get", "role"], "corner"],
        ]}
        id={GRID_HANDLE_CORNER_LAYER_ID}
        layout={{ visibility: gridVisibility }}
        paint={{
          "circle-color": "#00e5ff",
          "circle-radius": 7,
          "circle-stroke-color": "#000000",
          "circle-stroke-width": 1.5,
        }}
        source={GRID_SOURCE_ID}
        type="circle"
      />
      <Layer
        filter={["==", ["get", "kind"], "grid-label"]}
        id={GRID_LABEL_LAYER_ID}
        layout={{
          ...labelPlaqueLayout({
            iconImage: LABEL_PLAQUE_IMAGE_IDS.plot,
            iconRotationAlignment: "map",
          }),
          "symbol-placement": "line-center",
          "text-allow-overlap": true,
          "text-field": ["get", "label"],
          "text-ignore-placement": true,
          "text-rotation-alignment": "map",
          "text-size": 14,
          visibility: gridLabelVisibility,
        }}
        minzoom={PLOT_LABEL_MIN_ZOOM}
        paint={{
          "text-color": LABEL_FILL_COLOR,
        }}
        source={GRID_SOURCE_ID}
        type="symbol"
      />
      <Layer
        beforeId={GRID_LABEL_LAYER_ID}
        filter={["==", ["get", "kind"], "grid-label"]}
        id={SAVED_PLOTS_LABEL_LAYER_ID}
        layout={{
          ...labelPlaqueLayout({
            iconImage: LABEL_PLAQUE_IMAGE_IDS.plot,
            iconRotationAlignment: "map",
          }),
          "symbol-placement": "line-center",
          "text-allow-overlap": true,
          "text-field": ["get", "label"],
          "text-ignore-placement": true,
          "text-rotation-alignment": "map",
          "text-size": 14,
          visibility: plotLabelVisibility,
        }}
        minzoom={PLOT_LABEL_MIN_ZOOM}
        paint={{
          "icon-opacity": dimmedOpacity(1),
          "text-color": LABEL_FILL_COLOR,
          "text-opacity": dimmedOpacity(1),
        }}
        source={SAVED_PLOTS_SOURCE_ID}
        type="symbol"
      />
      <Layer
        beforeId={SAVED_PLOTS_LABEL_LAYER_ID}
        filter={["==", ["get", "kind"], "grid-row-label"]}
        id={GRID_ROW_LABEL_LAYER_ID}
        layout={{
          ...labelPlaqueLayout({
            iconImage: LABEL_PLAQUE_IMAGE_IDS.row,
            iconRotationAlignment: "map",
          }),
          "icon-rotate": ["get", "rotation"],
          "text-allow-overlap": true,
          "text-field": ["get", "label"],
          "text-ignore-placement": true,
          "text-rotate": ["get", "rotation"],
          "text-rotation-alignment": "map",
          "text-size": 14,
          visibility: gridRowLabelVisibility,
        }}
        minzoom={ROW_LABEL_MIN_ZOOM}
        paint={{
          "text-color": ROW_BOUNDARY_COLOR,
        }}
        source={GRID_SOURCE_ID}
        type="symbol"
      />
      <Layer
        beforeId={GRID_ROW_LABEL_LAYER_ID}
        filter={["==", ["get", "kind"], "row-label"]}
        id={ROW_BOUNDARY_LABEL_LAYER_ID}
        layout={{
          ...labelPlaqueLayout({
            iconImage: LABEL_PLAQUE_IMAGE_IDS.row,
            iconRotationAlignment: "map",
          }),
          "icon-rotate": ["get", "rotation"],
          "text-allow-overlap": true,
          "text-field": ["get", "label"],
          "text-ignore-placement": true,
          "text-rotate": ["get", "rotation"],
          "text-rotation-alignment": "map",
          "text-size": 14,
          visibility: rowLabelVisibility,
        }}
        minzoom={ROW_LABEL_MIN_ZOOM}
        paint={{
          "icon-opacity": dimmedOpacity(1),
          "text-color": ROW_BOUNDARY_COLOR,
          "text-opacity": dimmedOpacity(1),
        }}
        source={ROW_BOUNDARY_SOURCE_ID}
        type="symbol"
      />
      <Layer
        beforeId={ROW_BOUNDARY_LABEL_LAYER_ID}
        filter={["==", ["get", "kind"], "grid-trial-label"]}
        id={GRID_TRIAL_LABEL_LAYER_ID}
        layout={{
          ...labelPlaqueLayout({
            iconAnchor: "bottom",
            iconImage: LABEL_PLAQUE_IMAGE_IDS.trial,
          }),
          "text-allow-overlap": true,
          "text-anchor": "bottom",
          "text-field": ["get", "label"],
          "text-ignore-placement": true,
          "text-size": 16,
          visibility: gridVisibility,
        }}
        paint={{
          "icon-translate": [0, -GRID_TRIAL_LABEL_PIXEL_OFFSET],
          "icon-translate-anchor": "viewport",
          "text-color": LABEL_FILL_COLOR,
          "text-translate": [0, -GRID_TRIAL_LABEL_PIXEL_OFFSET],
          "text-translate-anchor": "viewport",
        }}
        source={GRID_SOURCE_ID}
        type="symbol"
      />
      <Layer
        beforeId={GRID_TRIAL_LABEL_LAYER_ID}
        filter={["==", ["get", "kind"], "grid-trial-label"]}
        id={SAVED_PLOTS_TRIAL_LABEL_LAYER_ID}
        layout={{
          ...labelPlaqueLayout({
            iconAnchor: "bottom",
            iconImage: LABEL_PLAQUE_IMAGE_IDS.trial,
          }),
          "text-allow-overlap": true,
          "text-anchor": "bottom",
          "text-field": ["get", "label"],
          "text-ignore-placement": true,
          "text-size": 16,
          visibility: plotGridVisibility,
        }}
        paint={{
          "icon-opacity": dimmedOpacity(1),
          "icon-translate": [0, -GRID_TRIAL_LABEL_PIXEL_OFFSET],
          "icon-translate-anchor": "viewport",
          "text-color": LABEL_FILL_COLOR,
          "text-opacity": dimmedOpacity(1),
          "text-translate": [0, -GRID_TRIAL_LABEL_PIXEL_OFFSET],
          "text-translate-anchor": "viewport",
        }}
        source={SAVED_PLOTS_SOURCE_ID}
        type="symbol"
      />
      <Layer
        beforeId={GRID_HANDLE_CORNER_LAYER_ID}
        filter={["==", ["get", "kind"], "grid-row-line"]}
        id={GRID_ROW_LINE_LAYER_ID}
        layout={{ visibility: gridRowVisibility }}
        paint={{
          "line-color": ROW_BOUNDARY_COLOR,
          "line-dasharray": [2, 2],
          "line-opacity": 1,
          "line-width": 2,
        }}
        source={GRID_SOURCE_ID}
        type="line"
      />
      <Layer
        beforeId={GRID_ROW_LINE_LAYER_ID}
        filter={["==", ["get", "kind"], "row-divider"]}
        id={ROW_BOUNDARY_LINE_LAYER_ID}
        layout={{ visibility: rowBoundaryVisibility }}
        paint={{
          "line-color": ROW_BOUNDARY_COLOR,
          "line-dasharray": [2, 2],
          "line-opacity": dimmedOpacity(1),
          "line-width": 2,
        }}
        source={ROW_BOUNDARY_SOURCE_ID}
        type="line"
      />
      <Layer
        beforeId={ROW_BOUNDARY_LINE_LAYER_ID}
        filter={["==", ["get", "kind"], "grid-line"]}
        id={GRID_LINE_LAYER_ID}
        layout={{ visibility: gridVisibility }}
        paint={{
          "line-color": "#00e5ff",
          "line-opacity": 0.95,
          "line-width": 2,
        }}
        source={GRID_SOURCE_ID}
        type="line"
      />
      <Layer
        beforeId={GRID_LINE_LAYER_ID}
        filter={["==", ["get", "kind"], "grid-cell"]}
        id={GRID_CELL_LAYER_ID}
        layout={{ visibility: gridVisibility }}
        paint={{
          "fill-color": plotFillColor(true),
          "fill-opacity": [
            "case",
            ["get", "selected"],
            0.6,
            ["has", "fillColor"],
            COLORED_PLOT_FILL_OPACITY,
            DEFAULT_PLOT_FILL_OPACITY,
          ],
        }}
        source={GRID_SOURCE_ID}
        type="fill"
      />
      <Layer
        beforeId={GRID_CELL_LAYER_ID}
        filter={["==", ["get", "kind"], "grid-line"]}
        id={SAVED_PLOTS_LINE_LAYER_ID}
        layout={{ visibility: plotGridVisibility }}
        paint={{
          "line-color": "#00e5ff",
          "line-opacity": dimmedOpacity(0.95),
          "line-width": 2,
        }}
        source={SAVED_PLOTS_SOURCE_ID}
        type="line"
      />
      <Layer
        beforeId={SAVED_PLOTS_LINE_LAYER_ID}
        filter={["==", ["get", "kind"], "row-fill"]}
        id={ROW_FILL_LAYER_ID}
        layout={{ visibility: rowBoundaryVisibility }}
        paint={{
          "fill-color": ["get", "fillColor"],
          "fill-opacity": dimmedOpacity(COLORED_PLOT_FILL_OPACITY),
        }}
        source={ROW_FILL_SOURCE_ID}
        type="fill"
      />
      <Layer
        beforeId={ROW_FILL_LAYER_ID}
        filter={["==", ["get", "kind"], "grid-cell"]}
        id={SAVED_PLOTS_FILL_LAYER_ID}
        layout={{ visibility: plotGridVisibility }}
        paint={{
          "fill-color": plotFillColor(),
          "fill-opacity": [
            "case",
            ["has", "fillColor"],
            dimmedOpacity(COLORED_PLOT_FILL_OPACITY),
            dimmedOpacity(DEFAULT_PLOT_FILL_OPACITY),
          ],
        }}
        source={SAVED_PLOTS_SOURCE_ID}
        type="fill"
      />
      <Layer
        beforeId={SAVED_PLOTS_FILL_LAYER_ID}
        filter={[
          "all",
          ["==", ["get", "kind"], "vertex"],
          ["==", ["get", "role"], "first"],
        ]}
        id={SITE_BOUNDARY_FIRST_POINT_LAYER_ID}
        layout={{ visibility: siteDraftVisibility }}
        paint={{
          "circle-color": SITE_BOUNDARY_COLOR,
          "circle-opacity": 1,
          "circle-radius": 8,
          "circle-stroke-color": "#ffffff",
          "circle-stroke-opacity": 1,
          "circle-stroke-width": 2,
        }}
        source={SITE_BOUNDARY_DRAFT_SOURCE_ID}
        type="circle"
      />
      <Layer
        beforeId={SITE_BOUNDARY_FIRST_POINT_LAYER_ID}
        filter={[
          "all",
          ["==", ["get", "kind"], "vertex"],
          ["==", ["get", "role"], "vertex"],
        ]}
        id={SITE_BOUNDARY_VERTEX_LAYER_ID}
        layout={{ visibility: siteDraftVisibility }}
        paint={{
          "circle-color": SITE_BOUNDARY_COLOR,
          "circle-opacity": 1,
          "circle-radius": 5,
          "circle-stroke-color": "#000000",
          "circle-stroke-opacity": 1,
          "circle-stroke-width": 1.5,
        }}
        source={SITE_BOUNDARY_DRAFT_SOURCE_ID}
        type="circle"
      />
      <Layer
        beforeId={SAVED_PLOTS_TRIAL_LABEL_LAYER_ID}
        filter={["==", ["get", "kind"], "label"]}
        id={SITE_BOUNDARY_DRAFT_LABEL_LAYER_ID}
        layout={{
          ...labelPlaqueLayout({
            iconImage: LABEL_PLAQUE_IMAGE_IDS.site,
            iconRotationAlignment: "map",
          }),
          "symbol-placement": "line",
          "text-allow-overlap": true,
          "text-field": ["get", "label"],
          "text-ignore-placement": true,
          "text-rotation-alignment": "map",
          "text-size": 18,
          visibility: siteDraftLabelVisibility,
        }}
        paint={{
          "text-color": LABEL_FILL_COLOR,
        }}
        source={SITE_BOUNDARY_DRAFT_SOURCE_ID}
        type="symbol"
      />
      <Layer
        beforeId={SITE_BOUNDARY_VERTEX_LAYER_ID}
        filter={[
          "any",
          ["==", ["get", "kind"], "line"],
          ["==", ["get", "kind"], "polygon"],
        ]}
        id={SITE_BOUNDARY_DRAFT_LINE_LAYER_ID}
        layout={{ visibility: siteDraftVisibility }}
        paint={{
          "line-color": SITE_BOUNDARY_COLOR,
          "line-opacity": 1,
          "line-width": 2,
        }}
        source={SITE_BOUNDARY_DRAFT_SOURCE_ID}
        type="line"
      />
      <Layer
        beforeId={SITE_BOUNDARY_DRAFT_LABEL_LAYER_ID}
        filter={["==", ["get", "kind"], "label"]}
        id={SITE_BOUNDARY_LABEL_LAYER_ID}
        layout={{
          ...labelPlaqueLayout({
            iconImage: LABEL_PLAQUE_IMAGE_IDS.site,
            iconRotationAlignment: "map",
          }),
          "symbol-placement": "line",
          "text-allow-overlap": true,
          "text-field": ["get", "label"],
          "text-ignore-placement": true,
          "text-rotation-alignment": "map",
          "text-size": 18,
          visibility: siteLabelVisibility,
        }}
        paint={{
          "icon-opacity": dimmedOpacity(1),
          "text-color": LABEL_FILL_COLOR,
          "text-opacity": dimmedOpacity(1),
        }}
        source={SITE_BOUNDARY_SOURCE_ID}
        type="symbol"
      />
      <Layer
        beforeId={SITE_BOUNDARY_LABEL_LAYER_ID}
        filter={["==", ["get", "kind"], "label"]}
        id={BOUNDARY_LABEL_LAYER_ID}
        layout={{
          ...labelPlaqueLayout({
            iconImage: LABEL_PLAQUE_IMAGE_IDS.grower,
            iconRotationAlignment: "map",
          }),
          "symbol-placement": "line",
          "text-allow-overlap": true,
          "text-field": ["get", "label"],
          "text-ignore-placement": true,
          "text-rotation-alignment": "map",
          "text-size": 18,
          visibility: growerLabelVisibility,
        }}
        paint={{
          "icon-opacity": dimmedOpacity(1),
          "text-color": LABEL_FILL_COLOR,
          "text-opacity": dimmedOpacity(1),
        }}
        source={BOUNDARY_SOURCE_ID}
        type="symbol"
      />
      <Layer
        beforeId={SITE_BOUNDARY_DRAFT_LINE_LAYER_ID}
        filter={["==", ["get", "kind"], "polygon"]}
        id={SITE_BOUNDARY_LINE_LAYER_ID}
        layout={{ visibility: siteBoundaryVisibility }}
        paint={{
          "line-color": SITE_BOUNDARY_COLOR,
          "line-opacity": dimmedOpacity(1),
          "line-width": 2,
        }}
        source={SITE_BOUNDARY_SOURCE_ID}
        type="line"
      />
      <Layer
        beforeId={SITE_BOUNDARY_LINE_LAYER_ID}
        filter={[
          "all",
          ["==", ["get", "kind"], "vertex"],
          ["==", ["get", "role"], "first"],
        ]}
        id={BOUNDARY_FIRST_POINT_LAYER_ID}
        layout={{ visibility: growerDraftVisibility }}
        paint={{
          "circle-color": BOUNDARY_COLOR,
          "circle-opacity": 1,
          "circle-radius": 8,
          "circle-stroke-color": "#ffffff",
          "circle-stroke-opacity": 1,
          "circle-stroke-width": 2,
        }}
        source={GROWER_BOUNDARY_DRAFT_SOURCE_ID}
        type="circle"
      />
      <Layer
        beforeId={BOUNDARY_FIRST_POINT_LAYER_ID}
        filter={[
          "all",
          ["==", ["get", "kind"], "vertex"],
          ["==", ["get", "role"], "vertex"],
        ]}
        id={BOUNDARY_VERTEX_LAYER_ID}
        layout={{ visibility: growerDraftVisibility }}
        paint={{
          "circle-color": BOUNDARY_COLOR,
          "circle-opacity": 1,
          "circle-radius": 5,
          "circle-stroke-color": "#000000",
          "circle-stroke-opacity": 1,
          "circle-stroke-width": 1.5,
        }}
        source={GROWER_BOUNDARY_DRAFT_SOURCE_ID}
        type="circle"
      />
      <Layer
        beforeId={BOUNDARY_LABEL_LAYER_ID}
        filter={["==", ["get", "kind"], "label"]}
        id={GROWER_BOUNDARY_DRAFT_LABEL_LAYER_ID}
        layout={{
          ...labelPlaqueLayout({
            iconImage: LABEL_PLAQUE_IMAGE_IDS.grower,
            iconRotationAlignment: "map",
          }),
          "symbol-placement": "line",
          "text-allow-overlap": true,
          "text-field": ["get", "label"],
          "text-ignore-placement": true,
          "text-rotation-alignment": "map",
          "text-size": 18,
          visibility: growerDraftLabelVisibility,
        }}
        paint={{
          "text-color": LABEL_FILL_COLOR,
        }}
        source={GROWER_BOUNDARY_DRAFT_SOURCE_ID}
        type="symbol"
      />
      <Layer
        beforeId={BOUNDARY_VERTEX_LAYER_ID}
        filter={[
          "any",
          ["==", ["get", "kind"], "line"],
          ["==", ["get", "kind"], "polygon"],
        ]}
        id={GROWER_BOUNDARY_DRAFT_LINE_LAYER_ID}
        layout={{ visibility: growerDraftVisibility }}
        paint={{
          "line-color": BOUNDARY_COLOR,
          "line-opacity": 1,
          "line-width": 2,
        }}
        source={GROWER_BOUNDARY_DRAFT_SOURCE_ID}
        type="line"
      />
      <Layer
        beforeId={GROWER_BOUNDARY_DRAFT_LINE_LAYER_ID}
        filter={["==", ["get", "kind"], "polygon"]}
        id={BOUNDARY_LINE_LAYER_ID}
        layout={{ visibility: boundaryVisibility }}
        paint={{
          "line-color": BOUNDARY_COLOR,
          "line-opacity": dimmedOpacity(1),
          "line-width": 2,
        }}
        source={BOUNDARY_SOURCE_ID}
        type="line"
      />
    </>
  );
};

export default PlotMapLayers;
